import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { GoogleMap, Marker, Circle } from "@react-google-maps/api";
import { Loader2 } from "lucide-react";
import { useGoogleMaps } from "@/context/GoogleMapsContext";
import AutocompleteLocation from "@/taxi-dispatch/dispatch/components/AutocompleteLocation";
import axiosInstance from "@/utils/axiosInstance";
import { grayMapStyle } from "@/utils/variables";

interface Location {
  id?: number;
  text: string;
  lat: number;
  lng: number;
}

import { ServiceLocation } from "./ServiceLocationsList";

interface RadiusLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location?: ServiceLocation | null;
  mode: "create" | "edit";
  onSuccess: () => void;
}

export function RadiusLocationDialog({
  open,
  onOpenChange,
  location,
  mode,
  onSuccess,
}: RadiusLocationDialogProps) {
  const { isLoaded, loadError } = useGoogleMaps();
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [formData, setFormData] = useState({
    id: 0,
    settings_id: 0,
    name: "",
    alias: "",
    type: "RADIUS" as const,
    coordinates: [] as Array<{ lat: number; lng: number; name?: string }>,
    radius: 1, // default 1km radius
    description: "",
    created_at: "",
    updated_at: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMapPosition, setSelectedMapPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [radiusValue, setRadiusValue] = useState([1]); // Slider value array

  // Map center (you can adjust this to your preferred default location)
  const mapCenter = { lat: 41.3275, lng: 19.8187 }; // Tirana, Albania

  // Update form data when location prop changes
  useEffect(() => {
    if (location && mode === "edit" && location.type === "RADIUS") {
      // When editing, check if the name is stored in coordinates (meaning it's an alias)
      const hasLocationNameInCoords = location.coordinates.length > 0 && 
        location.coordinates[0].name && 
        location.coordinates[0].name !== location.name;
      
      setFormData({
        ...location,
        alias: hasLocationNameInCoords ? location.name : "",
        name: hasLocationNameInCoords ? location.coordinates[0].name || "" : location.name,
        type: "RADIUS",
        radius: location.radius || 1,
        description: location.description || "",
      });
      setRadiusValue([location.radius || 1]);
      if (location.coordinates.length > 0) {
        const coord = location.coordinates[0];
        setSelectedMapPosition({ lat: coord.lat, lng: coord.lng });
      }
    } else {
      // Reset form for create mode
      setFormData({
        id: 0,
        settings_id: 0,
        name: "",
        alias: "",
        type: "RADIUS",
        coordinates: [],
        radius: 1,
        description: "",
        created_at: "",
        updated_at: "",
      });
      setRadiusValue([1]);
      setSelectedMapPosition(null);
    }
    setErrors({});
  }, [location, mode, open]);

  // Update radius in form data when slider changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, radius: radiusValue[0] }));
  }, [radiusValue]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Starting point is required";
    }

    if (!formData.coordinates.length) {
      newErrors.coordinates = "Please select a location on the map";
    }

    if (formData.radius <= 0) {
      newErrors.radius = "Radius must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // If alias is set, use alias as name and store original name in coordinates
      // If no alias, use the location name as the service location name
      const finalName = formData.alias.trim() || formData.name.trim();
      const coordinatesWithName = formData.coordinates.map(coord => ({
        ...coord,
        name: formData.alias.trim() ? formData.name : coord.name
      }));

      const payload = {
        name: finalName,
        type: formData.type,
        coordinates: coordinatesWithName,
        radius: formData.radius,
        description: formData.description || null,
      };

      if (mode === "create") {
        await axiosInstance.post("/route-areas", payload);
      } else if (location?.id) {
        await axiosInstance.put(`/route-areas/${location.id}`, payload);
      }

      onSuccess();
    } catch (error) {
      console.error("Error saving radius location:", error);
      // You can add toast notification here
    } finally {
      setIsLoading(false);
    }
  };

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setSelectedMapPosition({ lat, lng });

        // Reverse geocoding to get location name
        if (window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results && results.length > 0) {
              const locationName = results[0].formatted_address;
              handleInputChange("name", locationName);
              handleInputChange("coordinates", [
                { lat, lng, name: locationName },
              ]);
              // Update the autocomplete input value
              if (nameInputRef.current) {
                nameInputRef.current.value = locationName;
              }
            } else {
              // Fallback if geocoding fails
              handleInputChange("coordinates", [{ lat, lng }]);
            }
          });
        } else {
          // Fallback if geocoding is not available
          handleInputChange("coordinates", [{ lat, lng }]);
        }
      }
    },
    [handleInputChange]
  );

  const handleLocationSelect = (locationData: Location) => {
    handleInputChange("name", locationData.text);
    const coordinates = [
      { lat: locationData.lat, lng: locationData.lng, name: locationData.text },
    ];
    handleInputChange("coordinates", coordinates);
    setSelectedMapPosition({ lat: locationData.lat, lng: locationData.lng });
  };

  // Convert radius from km to meters for Google Maps Circle
  const radiusInMeters = formData.radius * 1000;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {mode === "create" ? "Create Radius Area" : "Edit Radius Area"}
            </DialogTitle>
            <DialogDescription>
              {mode === "create"
                ? "Select a point on the map and set the radius to create a circular area."
                : "Update the radius area details."}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 pr-4 overflow-auto">
            <form
              id="radius-location-form"
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="flex gap-4">
                <div className="flex-1 space-y-2">
                  <AutocompleteLocation
                    placeholder="Starting point"
                    location={handleLocationSelect}
                    inputRef={nameInputRef}
                    required={true}
                    selectedLocation={
                      formData.name
                        ? { text: formData.name, lat: 0, lng: 0 }
                        : undefined
                    }
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div className="w-64 space-y-2">
                  <Label htmlFor="alias">Location Alias (Optional)</Label>
                  <Input
                    id="alias"
                    placeholder="Enter custom alias"
                    value={formData.alias}
                    onChange={(e) => handleInputChange("alias", e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="radius">Radius: {formData.radius} km</Label>
                <div className="px-2">
                  <Slider
                    value={radiusValue}
                    onValueChange={setRadiusValue}
                    min={0.1}
                    max={50}
                    step={0.1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0.1 km</span>
                    <span>50 km</span>
                  </div>
                </div>
                {errors.radius && (
                  <p className="text-sm text-red-500">{errors.radius}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="coordinates">Location & Preview</Label>
                <div className="space-y-3">
                  {isLoaded && !loadError && (
                    <div className="border rounded-md overflow-hidden">
                      <GoogleMap
                        center={selectedMapPosition || mapCenter}
                        zoom={13}
                        mapContainerClassName="h-[400px] w-full"
                        onClick={handleMapClick}
                        options={{
                          styles: grayMapStyle,
                          fullscreenControl: false,
                          streetViewControl: false,
                          mapTypeControl: false,
                          gestureHandling: "greedy",
                        }}
                      >
                        {selectedMapPosition && (
                          <>
                            <Marker position={selectedMapPosition} />
                            <Circle
                              center={selectedMapPosition}
                              radius={radiusInMeters}
                              options={{
                                fillColor: "#4A90E2",
                                fillOpacity: 0.2,
                                strokeColor: "#4A90E2",
                                strokeOpacity: 0.8,
                                strokeWeight: 2,
                              }}
                            />
                          </>
                        )}
                      </GoogleMap>
                      <div className="p-2 bg-gray-50 text-sm text-gray-600">
                        Click on the map to select the center point
                        {selectedMapPosition && (
                          <span className="ml-2 font-mono">
                            ({selectedMapPosition.lat.toFixed(6)},{" "}
                            {selectedMapPosition.lng.toFixed(6)}) - Radius: {formData.radius} km
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {loadError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-600">
                        Error loading map: {loadError.message}
                      </p>
                    </div>
                  )}

                  {!isLoaded && (
                    <div className="p-4 bg-gray-50 border rounded-md">
                      <p className="text-gray-600">Loading map...</p>
                    </div>
                  )}
                </div>
                {errors.coordinates && (
                  <p className="text-sm text-red-500">{errors.coordinates}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Enter description"
                  className="resize-none"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                />
              </div>
            </form>
          </ScrollArea>

          <DialogFooter className="mt-4 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="radius-location-form"
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "create" ? "Create" : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}